import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

function formatMinutes(minutes = 0) {
  const safeMinutes = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  if (!hours) return `${remainder}m`;
  if (!remainder) return `${hours}h`;
  return `${hours}h ${String(remainder).padStart(2, '0')}m`;
}

function formatSignedMinutes(minutes = 0) {
  const numericValue = Number(minutes) || 0;
  const signal = numericValue < 0 ? '-' : '';
  return `${signal}${formatMinutes(Math.abs(numericValue))}`;
}

function formatDateLabel(value) {
  if (!value) return 'Sem prazo';
  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? 'Sem prazo' : parsed.toLocaleDateString('pt-BR');
}

function completionTone(value) {
  if (value >= 80) return 'bg-emerald-500';
  if (value >= 55) return 'bg-amber-500';
  return 'bg-rose-500';
}

function riskTone(impact) {
  if (impact === 'high') return 'border-rose-200 bg-rose-50 text-rose-700';
  if (impact === 'medium') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-emerald-200 bg-emerald-50 text-emerald-700';
}

function MetricCard({ label, value, detail, tone }) {
  return (
    <Card className={`overflow-hidden border-0 shadow-sm ${tone}`}>
      <CardContent className="p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] opacity-75">{label}</p>
        <p className="mt-4 text-3xl font-bold tracking-tight">{value}</p>
        <p className="mt-2 text-sm opacity-80">{detail}</p>
      </CardContent>
    </Card>
  );
}

function DistributionBar({ label, count, percentage, tone }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="text-slate-500">{count} · {percentage}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(0, Math.min(100, percentage || 0))}%` }} />
      </div>
    </div>
  );
}

export default function ProjectAnalytics({ board, api, isOpen, onClose }) {
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [activeTab, setActiveTab] = useState('executive');
  const [selectedReportPreset, setSelectedReportPreset] = useState('overview');

  const timeRanges = [
    { value: '7d', label: 'Ultimos 7 dias' },
    { value: '30d', label: 'Ultimos 30 dias' },
    { value: '90d', label: 'Ultimos 90 dias' },
    { value: '1y', label: 'Ultimo ano' },
    { value: 'all', label: 'Periodo amplo' },
  ];

  useEffect(() => {
    if (isOpen && board) {
      fetchAnalytics();
    }
  }, [isOpen, board, timeRange]);

  const fetchAnalytics = async () => {
    if (!board) return;
    setLoading(true);
    try {
      const response = await api.get(`/projects/boards/${board.id}/analytics`, {
        params: { time_range: timeRange, report_type: activeTab },
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics(null);
      toast.error('Erro ao carregar relatorios do board');
    } finally {
      setLoading(false);
    }
  };

  const reportTypeForExport = useMemo(() => {
    if (activeTab === 'reports') return selectedReportPreset;
    const mapping = {
      executive: 'overview',
      flow: 'productivity',
      team: 'team_performance',
      risks: 'milestone_analysis',
    };
    return mapping[activeTab] || 'overview';
  }, [activeTab, selectedReportPreset]);

  const reportPresets = useMemo(() => ([
    {
      key: 'overview',
      icon: '📊',
      label: 'Snapshot executivo',
      detail: 'Resumo geral do board com progresso, prazo, bloqueios e carga.',
    },
    {
      key: 'board_items',
      icon: '📋',
      label: 'Tabela principal',
      detail: 'Extrai a estrutura da main table com grupos, itens e colunas do quadro.',
    },
    {
      key: 'time_tracking',
      icon: '⏱️',
      label: 'Tempo rastreado',
      detail: 'Foco em planejado vs rastreado por item, prazo e responsáveis.',
    },
    {
      key: 'team_performance',
      icon: '👥',
      label: 'Carga por responsável',
      detail: 'Distribuição real de volume, atrasos e conclusão por pessoa.',
    },
    {
      key: 'productivity',
      icon: '📈',
      label: 'Histórico de atividade',
      detail: 'Mostra criação, conclusão e atualização na janela selecionada.',
    },
    {
      key: 'milestone_analysis',
      icon: '🚨',
      label: 'Riscos e gargalos',
      detail: 'Expõe pontos de pressão, itens críticos e bloqueios operacionais.',
    },
  ]), []);

  const scheduleReport = async (reportType, frequency) => {
    if (!board) return;
    setScheduling(true);
    try {
      await api.post(`/projects/boards/${board.id}/schedule-report`, {
        report_type: reportType,
        frequency,
        recipients: [],
      });
      toast.success(`Relatorio ${frequency === 'weekly' ? 'semanal' : 'mensal'} agendado`);
    } catch (error) {
      console.error('Error scheduling report:', error);
      toast.error('Erro ao agendar relatorio');
    } finally {
      setScheduling(false);
    }
  };

  const exportReport = async (format) => {
    if (!board) return;
    setExporting(true);
    try {
      const response = await api.post(
        `/projects/boards/${board.id}/export-report`,
        { format, time_range: timeRange, report_type: reportTypeForExport },
        { responseType: 'blob' }
      );

      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const extension = format === 'excel' ? 'xlsx' : format;
      const filename = match ? match[1] : `relatorio.${extension}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Relatorio ${format.toUpperCase()} exportado com sucesso`);
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Erro ao exportar relatorio');
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  const overview = analytics?.overview || {};
  const statusDistribution = analytics?.status_distribution || [];
  const priorityDistribution = analytics?.priority_distribution || [];
  const groupPerformance = analytics?.group_performance || [];
  const teamPerformance = analytics?.team_performance || [];
  const riskItems = analytics?.risk_items || [];
  const bottlenecks = analytics?.bottlenecks || [];
  const dueBuckets = analytics?.due_buckets || {};
  const timeline = analytics?.trends?.activity_timeline || [];
  const workloadItems = analytics?.workload_items || [];
  const recentActivity = analytics?.recent_activity || {};
  const operationalInsights = analytics?.operational_insights || [];
  const selectedPreset = reportPresets.find(preset => preset.key === selectedReportPreset) || reportPresets[0];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex h-[92vh] max-h-[92vh] w-[calc(100vw-1rem)] max-w-[1320px] flex-col overflow-hidden border-slate-200 p-0 sm:w-[calc(100vw-2rem)]"
        closeClassName="text-white opacity-90 hover:opacity-100 hover:bg-white/10 data-[state=open]:bg-white/10 data-[state=open]:text-white"
      >
        <DialogHeader className="shrink-0 border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.16),_transparent_30%),linear-gradient(135deg,_#0f172a,_#1e293b_58%,_#334155)] px-4 py-5 text-white sm:px-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <DialogTitle className="text-2xl font-semibold">Relatorios operacionais</DialogTitle>
              <p className="mt-2 text-sm text-slate-300">
                Leitura real do board com tarefas, subtarefas, workload, gargalos e risco de prazo.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="rounded-full border-white/15 bg-white/10 px-3 py-1 text-white">
                {board?.name}
              </Badge>
              <Badge variant="outline" className="rounded-full border-white/15 bg-white/10 px-3 py-1 text-white">
                {timeRanges.find(range => range.value === timeRange)?.label}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="flex flex-wrap gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Janela de atividade</label>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger className="w-48 rounded-xl border-slate-200 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeRanges.map(range => (
                      <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={fetchAnalytics} disabled={loading}>
                Atualizar dados
              </Button>
              <Button variant="outline" onClick={() => exportReport('csv')} disabled={exporting}>
                CSV
              </Button>
              <Button variant="outline" onClick={() => exportReport('excel')} disabled={exporting}>
                Excel
              </Button>
              <Button variant="outline" onClick={() => exportReport('pdf')} disabled={exporting}>
                PDF
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex h-72 items-center justify-center rounded-3xl border border-slate-200 bg-white">
              <div className="text-center">
                <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                <p className="text-sm text-slate-500">Gerando leitura operacional do board...</p>
              </div>
            </div>
          ) : !analytics ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
              Nao foi possivel carregar os relatorios deste board.
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
                <MetricCard
                  label="Escopo"
                  value={overview.total_items || 0}
                  detail={`${overview.primary_tasks || 0} tarefas pai · ${overview.subtasks || 0} subtarefas`}
                  tone="bg-slate-950 text-white"
                />
                <MetricCard
                  label="Conclusao"
                  value={`${overview.completion_rate || 0}%`}
                  detail={`${overview.completed_items || 0} concluidos`}
                  tone="bg-[linear-gradient(135deg,_#0ea5e9,_#2563eb)] text-white"
                />
                <MetricCard
                  label="Atrasados"
                  value={overview.overdue_items || 0}
                  detail={`${dueBuckets.today || 0} vencem hoje`}
                  tone="bg-[linear-gradient(135deg,_#f59e0b,_#f97316)] text-white"
                />
                <MetricCard
                  label="Bloqueios"
                  value={overview.blocked_items || 0}
                  detail={`${overview.in_progress_items || 0} em progresso`}
                  tone="bg-[linear-gradient(135deg,_#fb7185,_#e11d48)] text-white"
                />
                <MetricCard
                  label="Carga"
                  value={formatMinutes(overview.planned_minutes || 0)}
                  detail={`${formatMinutes(overview.tracked_minutes || 0)} rastreados`}
                  tone="bg-[linear-gradient(135deg,_#10b981,_#22c55e)] text-slate-950"
                />
                <MetricCard
                  label="Equipe"
                  value={overview.active_members || 0}
                  detail={`${overview.unassigned_items || 0} itens sem responsavel`}
                  tone="bg-[linear-gradient(135deg,_#ede9fe,_#dbeafe)] text-slate-900"
                />
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-2xl bg-slate-100 p-1 sm:grid-cols-3 lg:grid-cols-5">
                  <TabsTrigger value="executive" className="rounded-xl">Executivo</TabsTrigger>
                  <TabsTrigger value="flow" className="rounded-xl">Fluxo</TabsTrigger>
                  <TabsTrigger value="team" className="rounded-xl">Equipe</TabsTrigger>
                  <TabsTrigger value="risks" className="rounded-xl">Riscos</TabsTrigger>
                  <TabsTrigger value="reports" className="rounded-xl">Relatórios</TabsTrigger>
                </TabsList>

                <TabsContent value="executive" className="mt-6 space-y-6">
                  <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Distribuicao operacional</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Como o board esta composto agora</h3>
                      </div>

                      <div className="mt-5 space-y-4">
                        {statusDistribution.map(status => (
                          <DistributionBar
                            key={status.key}
                            label={status.name}
                            count={status.count}
                            percentage={status.percentage}
                            tone={completionTone(status.percentage)}
                          />
                        ))}
                      </div>

                      <div className="mt-6 border-t border-slate-200 pt-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Prioridades</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {priorityDistribution.map(priority => (
                            <span key={priority.key} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                              {priority.name}: {priority.count}
                            </span>
                          ))}
                        </div>
                      </div>
                    </section>

                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Pressao de prazo</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">O que exige prioridade de agenda</h3>
                      </div>

                      <div className="mt-5 grid grid-cols-2 gap-3">
                        {[
                          { label: 'Atrasados', value: dueBuckets.overdue || 0, tone: 'bg-rose-50 text-rose-700' },
                          { label: 'Hoje', value: dueBuckets.today || 0, tone: 'bg-amber-50 text-amber-700' },
                          { label: 'Esta semana', value: dueBuckets.this_week || 0, tone: 'bg-blue-50 text-blue-700' },
                          { label: 'Sem prazo', value: dueBuckets.without_due_date || 0, tone: 'bg-slate-100 text-slate-700' },
                        ].map(bucket => (
                          <div key={bucket.label} className={`rounded-2xl px-4 py-4 ${bucket.tone}`}>
                            <p className="text-xs font-semibold uppercase tracking-[0.14em]">{bucket.label}</p>
                            <p className="mt-2 text-3xl font-bold">{bucket.value}</p>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Atividade recente</p>
                        <div className="mt-3 grid gap-3 sm:grid-cols-3">
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{recentActivity.new_items_last_week || 0}</p>
                            <p className="text-xs text-slate-500">itens novos na janela recente</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{recentActivity.team_members || 0}</p>
                            <p className="text-xs text-slate-500">membros com atividade</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{recentActivity.avg_items_per_group || 0}</p>
                            <p className="text-xs text-slate-500">media de itens por grupo</p>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </TabsContent>

                <TabsContent value="flow" className="mt-6 space-y-6">
                  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Linha de atividade</p>
                      <h3 className="mt-1 text-xl font-semibold text-slate-900">Criacao, conclusao e movimentacao na janela selecionada</h3>
                    </div>

                    <div className="mt-5 space-y-4">
                      {timeline.map(bucket => {
                        const maxValue = Math.max(bucket.created || 0, bucket.completed || 0, bucket.updated || 0, 1);
                        return (
                          <article key={bucket.label} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                              <p className="font-semibold text-slate-900">{bucket.label}</p>
                              <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                                <span>Criados: {bucket.created}</span>
                                <span>Concluidos: {bucket.completed}</span>
                                <span>Atualizados: {bucket.updated}</span>
                              </div>
                            </div>

                            <div className="mt-4 space-y-3">
                              {[
                                { label: 'Criados', value: bucket.created, tone: 'bg-sky-500' },
                                { label: 'Concluidos', value: bucket.completed, tone: 'bg-emerald-500' },
                                { label: 'Atualizados', value: bucket.updated, tone: 'bg-slate-500' },
                              ].map(metric => (
                                <div key={metric.label} className="grid grid-cols-[86px_minmax(0,1fr)_48px] items-center gap-3">
                                  <span className="text-sm text-slate-600">{metric.label}</span>
                                  <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                                    <div className={`h-full rounded-full ${metric.tone}`} style={{ width: `${(metric.value / maxValue) * 100}%` }} />
                                  </div>
                                  <span className="text-right text-xs text-slate-500">{metric.value}</span>
                                </div>
                              ))}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>

                  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Leitura por grupo</p>
                      <h3 className="mt-1 text-xl font-semibold text-slate-900">Onde o fluxo anda e onde acumula pressao</h3>
                    </div>

                    <div className="mt-5 space-y-4">
                      {groupPerformance.map(group => (
                        <article key={group.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div className="min-w-0">
                              <div className="flex items-center gap-3">
                                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: group.color || '#2563EB' }} />
                                <h4 className="truncate font-semibold text-slate-900">{group.name}</h4>
                              </div>
                              <p className="mt-1 text-sm text-slate-500">
                                {group.total_items} itens · {group.primary_tasks} tarefas pai · {group.subtasks} subtarefas
                              </p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                              <div className="rounded-xl bg-white px-3 py-2 text-center">
                                <p className="text-[11px] uppercase tracking-wide text-slate-400">Progresso</p>
                                <p className="mt-1 font-semibold text-slate-900">{group.progress_percentage}%</p>
                              </div>
                              <div className="rounded-xl bg-white px-3 py-2 text-center">
                                <p className="text-[11px] uppercase tracking-wide text-slate-400">Bloqueios</p>
                                <p className="mt-1 font-semibold text-rose-700">{group.blocked_items}</p>
                              </div>
                              <div className="rounded-xl bg-white px-3 py-2 text-center">
                                <p className="text-[11px] uppercase tracking-wide text-slate-400">Atrasos</p>
                                <p className="mt-1 font-semibold text-amber-700">{group.overdue_items}</p>
                              </div>
                              <div className="rounded-xl bg-white px-3 py-2 text-center">
                                <p className="text-[11px] uppercase tracking-wide text-slate-400">Pressao</p>
                                <p className="mt-1 font-semibold text-slate-900">{group.pressure_score}</p>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200">
                            <div className={`h-full rounded-full ${completionTone(group.progress_percentage)}`} style={{ width: `${group.progress_percentage}%` }} />
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                </TabsContent>

                <TabsContent value="team" className="mt-6 space-y-6">
                  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Carga por responsavel</p>
                      <h3 className="mt-1 text-xl font-semibold text-slate-900">Quem carrega mais volume e onde pode haver desequilibrio</h3>
                    </div>

                    <div className="mt-5 space-y-3">
                      {teamPerformance.map(person => (
                        <article key={person.member} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-3">
                                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-bold text-white">
                                  {person.member?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                                <div>
                                  <p className="truncate font-semibold text-slate-900">{person.member}</p>
                                  <p className="text-xs text-slate-500">
                                    {person.count} itens · {person.subtasks} subtarefas · {person.completion_percentage}% concluidos
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="text-right text-sm">
                              <p className="font-semibold text-slate-900">{formatMinutes(person.planned_minutes)}</p>
                              <p className="text-xs text-slate-500">planejado</p>
                            </div>
                          </div>

                          <div className="mt-4 grid grid-cols-4 gap-2 text-center text-xs">
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Rastreado</p>
                              <p className="mt-1 font-semibold text-emerald-700">{formatMinutes(person.tracked_minutes)}</p>
                            </div>
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Gap</p>
                              <p className={`mt-1 font-semibold ${person.load_gap > 0 ? 'text-amber-700' : person.load_gap < 0 ? 'text-emerald-700' : 'text-slate-900'}`}>{formatSignedMinutes(person.load_gap)}</p>
                            </div>
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Atrasados</p>
                              <p className="mt-1 font-semibold text-amber-700">{person.overdue_items}</p>
                            </div>
                            <div className="rounded-xl bg-white px-2 py-2">
                              <p className="text-slate-400">Participacao</p>
                              <p className="mt-1 font-semibold text-slate-900">{person.participation_percentage}%</p>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                </TabsContent>

                <TabsContent value="risks" className="mt-6 space-y-6">
                  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Gargalos reais</p>
                      <h3 className="mt-1 text-xl font-semibold text-slate-900">Locais que travam o board agora</h3>
                    </div>

                    <div className="mt-5 space-y-3">
                      {bottlenecks.map(bottleneck => (
                        <article key={`${bottleneck.type}-${bottleneck.name}`} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${riskTone(bottleneck.impact)}`}>
                                  {bottleneck.type === 'group' ? 'Grupo' : 'Responsavel'}
                                </span>
                                <p className="truncate font-semibold text-slate-900">{bottleneck.name}</p>
                              </div>
                              <p className="mt-2 text-sm text-slate-500">{bottleneck.detail}</p>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center text-xs">
                              <div className="rounded-xl bg-white px-2 py-2">
                                <p className="text-slate-400">Pressao</p>
                                <p className="mt-1 font-semibold text-slate-900">{bottleneck.pressure_score}</p>
                              </div>
                              <div className="rounded-xl bg-white px-2 py-2">
                                <p className="text-slate-400">Bloqueios</p>
                                <p className="mt-1 font-semibold text-rose-700">{bottleneck.blocked_items}</p>
                              </div>
                              <div className="rounded-xl bg-white px-2 py-2">
                                <p className="text-slate-400">Atrasos</p>
                                <p className="mt-1 font-semibold text-amber-700">{bottleneck.overdue_items}</p>
                              </div>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>

                  <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Itens criticos</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Tarefas pai com maior risco operacional</h3>
                      </div>

                      <div className="mt-5 space-y-3">
                        {riskItems.map(item => (
                          <article key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate font-semibold text-slate-900">{item.name}</p>
                                <p className="mt-1 text-xs text-slate-500">{item.group_name}</p>
                              </div>
                              <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                                risco {item.risk_score}
                              </span>
                            </div>

                            <div className="mt-4 grid grid-cols-4 gap-2 text-center text-xs">
                              <div className="rounded-xl bg-white px-2 py-2">
                                <p className="text-slate-400">Bloqueios</p>
                                <p className="mt-1 font-semibold text-rose-700">{item.blocked_subtasks}</p>
                              </div>
                              <div className="rounded-xl bg-white px-2 py-2">
                                <p className="text-slate-400">Atrasos</p>
                                <p className="mt-1 font-semibold text-amber-700">{item.overdue_subtasks}</p>
                              </div>
                              <div className="rounded-xl bg-white px-2 py-2">
                                <p className="text-slate-400">Abertas</p>
                                <p className="mt-1 font-semibold text-slate-900">{item.open_subtasks}</p>
                              </div>
                              <div className="rounded-xl bg-white px-2 py-2">
                                <p className="text-slate-400">Prazo</p>
                                <p className="mt-1 font-semibold text-slate-900">{formatDateLabel(item.next_due_date)}</p>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    </section>

                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Sintese operacional</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">O que vale agir agora</h3>
                      </div>

                      <div className="mt-5 space-y-3">
                        {operationalInsights.map((insight, index) => (
                          <div key={index} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700">
                            {insight}
                          </div>
                        ))}
                      </div>
                    </section>
                  </div>
                </TabsContent>

                <TabsContent value="reports" className="mt-6 space-y-6">
                  <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Catálogo de relatórios</p>
                        <h3 className="mt-1 text-xl font-semibold text-slate-900">Cobertura completa sem voltar ao layout antigo</h3>
                      </div>

                      <div className="mt-5 space-y-3">
                        {reportPresets.map(preset => (
                          <button
                            key={preset.key}
                            type="button"
                            onClick={() => setSelectedReportPreset(preset.key)}
                            className={`w-full rounded-2xl border p-4 text-left transition-colors ${
                              selectedReportPreset === preset.key
                                ? 'border-blue-200 bg-blue-50 ring-2 ring-blue-500/10'
                                : 'border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-white'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <span className="text-2xl">{preset.icon}</span>
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900">{preset.label}</p>
                                <p className="mt-1 text-sm text-slate-500">{preset.detail}</p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>

                      <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Agendamentos</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={scheduling}
                            onClick={() => scheduleReport(reportTypeForExport, 'weekly')}
                          >
                            Resumo semanal
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={scheduling}
                            onClick={() => scheduleReport(reportTypeForExport, 'monthly')}
                          >
                            Resumo mensal
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={scheduling}
                            onClick={() => scheduleReport('board_items', 'weekly')}
                          >
                            Planilha da tabela
                          </Button>
                        </div>
                      </div>
                    </section>

                    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">Preview do relatório</p>
                          <h3 className="mt-1 text-xl font-semibold text-slate-900">{selectedPreset.label}</h3>
                          <p className="mt-2 text-sm text-slate-500">{selectedPreset.detail}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" disabled={exporting} onClick={() => exportReport('csv')}>CSV</Button>
                          <Button variant="outline" disabled={exporting} onClick={() => exportReport('excel')}>Excel</Button>
                          <Button variant="outline" disabled={exporting} onClick={() => exportReport('pdf')}>PDF</Button>
                        </div>
                      </div>

                      {selectedReportPreset === 'overview' && (
                        <div className="mt-5 grid gap-4 md:grid-cols-2">
                          <Card className="rounded-3xl border-slate-200 shadow-none">
                            <CardContent className="p-5">
                              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Escopo e status</p>
                              <div className="mt-4 space-y-3 text-sm text-slate-600">
                                <p>{overview.total_items || 0} itens totais no board.</p>
                                <p>{overview.completed_items || 0} concluídos e {overview.open_items || 0} em aberto.</p>
                                <p>{overview.blocked_items || 0} bloqueados e {overview.overdue_items || 0} atrasados.</p>
                              </div>
                            </CardContent>
                          </Card>
                          <Card className="rounded-3xl border-slate-200 shadow-none">
                            <CardContent className="p-5">
                              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Síntese operacional</p>
                              <div className="mt-4 space-y-3 text-sm text-slate-600">
                                {operationalInsights.slice(0, 3).map((insight, index) => (
                                  <p key={index}>{insight}</p>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {selectedReportPreset === 'board_items' && (
                        <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50/70 p-5">
                          <p className="text-sm text-slate-600">
                            Este relatório preserva a estrutura da tabela principal do quadro, com grupos, itens e colunas exatamente no formato de exportação.
                          </p>
                          <div className="mt-4 grid gap-3 sm:grid-cols-3">
                            <div className="rounded-2xl bg-white px-4 py-4">
                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Tarefas pai</p>
                              <p className="mt-2 text-2xl font-bold text-slate-900">{overview.primary_tasks || 0}</p>
                            </div>
                            <div className="rounded-2xl bg-white px-4 py-4">
                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Subtarefas</p>
                              <p className="mt-2 text-2xl font-bold text-slate-900">{overview.subtasks || 0}</p>
                            </div>
                            <div className="rounded-2xl bg-white px-4 py-4">
                              <p className="text-xs uppercase tracking-[0.14em] text-slate-400">Grupos ativos</p>
                              <p className="mt-2 text-2xl font-bold text-slate-900">{groupPerformance.length}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {selectedReportPreset === 'time_tracking' && (
                        <div className="mt-5 space-y-3">
                          {workloadItems.slice(0, 10).map(item => {
                            const gap = (item.tracked_minutes || 0) - (item.planned_minutes || 0);
                            return (
                              <article key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                  <div className="min-w-0">
                                    <p className="truncate font-semibold text-slate-900">{item.name}</p>
                                    <p className="mt-1 text-xs text-slate-500">
                                      {item.group_title || 'Sem grupo'} · {item.item_type === 'subtask' ? 'Subtarefa' : 'Tarefa'}
                                      {item.parent_name ? ` · ${item.parent_name}` : ''}
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                                    <div className="rounded-xl bg-white px-2 py-2">
                                      <p className="text-slate-400">Planejado</p>
                                      <p className="mt-1 font-semibold text-slate-900">{formatMinutes(item.planned_minutes || 0)}</p>
                                    </div>
                                    <div className="rounded-xl bg-white px-2 py-2">
                                      <p className="text-slate-400">Rastreado</p>
                                      <p className="mt-1 font-semibold text-emerald-700">{formatMinutes(item.tracked_minutes || 0)}</p>
                                    </div>
                                    <div className="rounded-xl bg-white px-2 py-2">
                                      <p className="text-slate-400">Gap</p>
                                      <p className={`mt-1 font-semibold ${gap > 0 ? 'text-amber-700' : gap < 0 ? 'text-emerald-700' : 'text-slate-900'}`}>{formatSignedMinutes(gap)}</p>
                                    </div>
                                  </div>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      )}

                      {selectedReportPreset === 'team_performance' && (
                        <div className="mt-5 space-y-3">
                          {teamPerformance.slice(0, 8).map(person => (
                            <article key={person.member} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate font-semibold text-slate-900">{person.member}</p>
                                  <p className="mt-1 text-xs text-slate-500">
                                    {person.count} itens · {person.primary_tasks} tarefas pai · {person.subtasks} subtarefas
                                  </p>
                                </div>
                                <div className="text-right text-sm">
                                  <p className="font-semibold text-slate-900">{person.completion_percentage}%</p>
                                  <p className="text-xs text-slate-500">conclusão</p>
                                </div>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}

                      {selectedReportPreset === 'productivity' && (
                        <div className="mt-5 space-y-3">
                          {timeline.map(bucket => (
                            <article key={bucket.label} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                              <div className="flex items-center justify-between gap-3">
                                <p className="font-semibold text-slate-900">{bucket.label}</p>
                                <div className="flex gap-2 text-xs text-slate-500">
                                  <span>Criados {bucket.created}</span>
                                  <span>Concluídos {bucket.completed}</span>
                                  <span>Atualizados {bucket.updated}</span>
                                </div>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}

                      {selectedReportPreset === 'milestone_analysis' && (
                        <div className="mt-5 space-y-3">
                          {bottlenecks.slice(0, 6).map(bottleneck => (
                            <article key={`${bottleneck.type}-${bottleneck.name}`} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate font-semibold text-slate-900">{bottleneck.name}</p>
                                  <p className="mt-1 text-sm text-slate-500">{bottleneck.detail}</p>
                                </div>
                                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${riskTone(bottleneck.impact)}`}>
                                  pressão {bottleneck.pressure_score}
                                </span>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
